import classNames from 'classnames';
import {WithRouterProps} from 'react-router';
import React, {useEffect, useState} from 'react';

import appActions from 'client/AppActions';
import {callAction} from 'client/framework';
import ClassroomInvitations from './ClassroomInvitations/ClassroomInvitations';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {getClassroomsQuery} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import {
  HardCapStudentErrorModal,
  InvalidEmailModal
} from 'client/Classrooms/StudentDashboard/Modals';
import {history} from 'client/history';
import JoinClassroomModal from 'client/Classrooms/JoinClassroom/JoinClassroomModal';
import {addGenericErrorToast, LoadingSpinner} from '@albert-io/atomic';
import StudentHomepageHeader from './StudentHomepageHeader';
import StudentHomepageSidebarDesktop from './StudentHomepageSidebarDesktop';
import StudentHomepageSidebarMobile from './StudentHomepageSidebarMobile';
import StudentHomepageTabs from './StudentHomepageTabs';
import useBreakpoint from 'lib/hooks/useBreakpoint';
import usePrevious from 'lib/hooks/usePrevious';
import useStudent from './hooks/useStudent';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

interface Props extends PropsWithChildrenRequired, WithRouterProps {}

export default function StudentHomepage({children, location, params}: Props) {
  const {isSAndDown} = useBreakpoint();
  const lastIsSAnddown = usePrevious(isSAndDown);

  const isStudentLoading = useStudent();
  const [isMaybeRedirectingUser, setIsMaybeRedirectingUsing] = useState(false);

  const showLoadingSpinner = isStudentLoading || isMaybeRedirectingUser;

  const {classroomId} = params;

  const exceededHardCapLimit = useStudentDashboard((state) => state.exceededHardCapLimit);
  const hasInvitations = useStudentDashboard((state) => state.invitations.size > 0);
  const isSidebarExpanded = useStudentDashboard((state) => state.isSidebarExpanded);
  const setIsSidebarExpanded = useStudentDashboard((state) => state.setIsSidebarExpanded);
  const setShowJoinClassModal = useStudentDashboard((state) => state.setShowJoinClassModal);
  const showJoinClassModal = useStudentDashboard((state) => state.showJoinClassModal);
  const studentHasInvalidEmail = useStudentDashboard((state) => state.studentHasInvalidEmail);
  const studentId = useStudentDashboard((state) => state.student?.getId());

  const isWelcomePage = location.pathname.includes('welcome');
  const showTabs = !isWelcomePage;

  // Prevents the user from ending up on the wrong route when they have/don't have classrooms.
  useEffect(() => {
    if (studentId !== undefined) {
      setIsMaybeRedirectingUsing(true);

      getClassroomsQuery(studentId)
        .getResourcePromise()
        .then((classrooms) => {
          if (classrooms.size === 0) {
            history.replace('/home/welcome');
          } else {
            history.replace('/home/assignments');
          }
        })
        .catch(() => {
          addGenericErrorToast();
        })
        .finally(() => {
          setIsMaybeRedirectingUsing(false);
        });
    }
  }, [studentId]);

  useEffect(() => {
    callAction(appActions.SET_FOOTER_DISPLAY, false);

    return () => {
      callAction(appActions.SET_FOOTER_DISPLAY, true);
    };
  }, []);

  // Force sidebar to expand/contract when viewport changes to/from mobile
  useEffect(() => {
    if (isSAndDown !== lastIsSAnddown) {
      setIsSidebarExpanded(!isSAndDown);
    }
  }, [isSAndDown, lastIsSAnddown, setIsSidebarExpanded]);

  return (
    <FeatureFlag name='new_student_dashboard'>
      <div
        className={classNames(
          '-mt-[60px] flex h-[calc(100vh-60px)] flex-col background-subtle duration-200 m:grid m:grid-cols-[25%_auto] m:grid-rows-[repeat(3,min-content)_1fr]',
          {
            'justify-center': showLoadingSpinner,
            'm:grid-cols-[0_auto]!': !isSidebarExpanded
          }
        )}
      >
        {showLoadingSpinner ? (
          <LoadingSpinner
            className='m:col-span-full m:row-span-4 m:self-center'
            bottomMarginLevel={0}
            size={5}
          />
        ) : (
          <>
            <StudentHomepageHeader className='m:col-span-full' classroomId={classroomId} />
            <StudentHomepageSidebarDesktop className='m:row-span-3' pathname={location.pathname} />
            <ClassroomInvitations className='mx-x1 mt-x3 s:mx-x2' isWelcomePage={isWelcomePage} />
            {showTabs && (
              <StudentHomepageTabs
                className={classNames('mx-x1 shrink-0 s:mx-x2', {'mt-x3': !hasInvitations})}
                classroomId={classroomId}
                pathname={location.pathname}
              />
            )}
            <div
              className={classNames(
                'mx-x1 overflow-y-auto rounded-b-3xl background-primary s:mx-x2 m:max-h-full m:self-start',
                {
                  'm:mt-x4': !showTabs && hasInvitations,
                  'row-span-2': !showTabs || !hasInvitations,
                  'row-span-3 mt-x3': !showTabs && !hasInvitations,
                  'rounded-t-3xl': !showTabs
                }
              )}
              // Used to allow intersection observers to observe this as the root scrolling context
              id='tabContentContainer'
              // Unmounts and re-mounts the tab content when the classroom scope changes.
              // This significantly simplifies code since the dashboard tabs care a lot about the classroom scope.
              // See: https://react.dev/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes
              key={classroomId ?? 'home'}
            >
              {children}
            </div>
            {studentHasInvalidEmail && <InvalidEmailModal />}
            {exceededHardCapLimit && <HardCapStudentErrorModal />}
            {showJoinClassModal && (
              <JoinClassroomModal
                handleClose={() => setShowJoinClassModal(false)}
                isNewStudentDashboard
                onEnrollSuccess={() => {
                  setShowJoinClassModal(false);
                  if (isWelcomePage) {
                    history.replace('/home/assignments');
                  }
                }}
              />
            )}
          </>
        )}
      </div>
      <StudentHomepageSidebarMobile pathname={location.pathname} />
    </FeatureFlag>
  );
}
